/**
 * Q AI API Service Layer
 * Centralized API calls for Q AI functionality
 *
 * Endpoints:
 * - GET  /api/q/balance     - Token balance
 * - POST /api/q/ask         - Claude SSE streaming
 * - POST /api/q/auth/google - Google OAuth
 * - GET  /api/q/health      - Health check
 * - GET  /api/q/pricing     - Get pricing tiers & providers (F16)
 * - POST /api/q/topup       - Create payment checkout session (F16)
 */

import { baseURL, QBRIDGE_DOWNLOAD_URL, QBRIDGE_VERSION, QBRIDGE_SIZE, getQAIAdminConfig } from '../config';

// Get Q.AI Admin routing configuration for conversation history endpoints
const qaiAdminConfig = getQAIAdminConfig();
const qaiAdminBaseUrl = qaiAdminConfig.baseUrl;
const qaiAdminPathPrefix = qaiAdminConfig.pathPrefix;

// ═══════════════════════════════════════════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Token pricing tiers for PricingModal
 */
export const PRICING_TIERS = [
  { id: 'starter', name: 'Starter', price: 10, tokens: 500, popular: false },
  { id: 'standard', name: 'Standard', price: 25, tokens: 1500, popular: true },
  { id: 'pro', name: 'Pro', price: 50, tokens: 5000, popular: false }
];

/**
 * Token costs per action type
 */
export const ACTION_COSTS = {
  chat: 5,     // Simple Q&A
  analyze: 10, // EA Analysis / Session Breakdown
  signal: 15   // Q Scan / Trading Signals
};

/**
 * Processing steps with progress percentages (UI-6)
 */
export const PROCESSING_STEPS = {
  auth: { progress: 5, message: 'Authenticating...' },
  user: { progress: 15, message: 'Loading user profile...' },
  balance: { progress: 25, message: 'Checking token balance...' },
  sync_status: { progress: 35, message: 'Checking MT5 connection...' },
  signals: { progress: 50, message: 'Fetching market signals...' },
  strategies: { progress: 65, message: 'Loading trading performance...' },
  prompts: { progress: 75, message: 'Preparing AI context...' },
  generating: { progress: 85, message: 'Q is thinking...' }
};

// ═══════════════════════════════════════════════════════════════════════════
// AUTH ERROR HANDLER - Central 401/403 handling
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Handle 401/403 errors by clearing auth and redirecting to login
 * @param {number} status - HTTP status code
 * @param {string} context - Where the error occurred (for logging)
 * @returns {boolean} - true if was an auth error (caller should stop processing)
 */
const handleAuthError = (status, context = 'API') => {
  if (status === 401 || status === 403) {
    console.warn(`[qaiApi] Auth error (${status}) in ${context} - clearing session`);

    // Clear Q.AI auth tokens
    try {
      localStorage.removeItem('qai_token');
      localStorage.removeItem('qai_user');
      localStorage.removeItem('qai_current_conversation_id');
    } catch (e) {
      console.error('[qaiApi] Failed to clear localStorage:', e);
    }

    // Redirect to login (using window.location for reliability outside React context)
    // Only redirect if we're on a Q.AI page
    if (window.location.pathname.startsWith('/q-ai')) {
      window.location.href = '/q-ai/login?session=expired';
    }

    return true;
  }
  return false;
};

// ═══════════════════════════════════════════════════════════════════════════
// API FUNCTIONS
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Get user's current token balance (Task 4.6: also returns userId for history API)
 * @param {string} token - JWT token
 * @returns {Promise<{balance: number, isLow: boolean, totalUsed: number, userId: string|null}>}
 */
export const getBalance = async (token) => {
  const response = await fetch(`${baseURL}/api/q/balance`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  });

  if (!response.ok) {
    // Handle auth errors with redirect
    if (handleAuthError(response.status, 'getBalance')) {
      throw new Error('Session expired');
    }
    throw new Error(`Failed to get balance: ${response.status}`);
  }

  const data = await response.json();
  console.log('[qaiApi] getBalance raw response:', JSON.stringify(data));

  // Handle both direct response { balance: N } and wrapped { data: { balance: N } }
  const balanceData = data.data || data;
  const balanceValue = balanceData.balance ?? 0;

  return {
    balance: balanceValue,
    isLow: balanceValue <= 15,
    totalUsed: balanceData.totalUsed || 0,
    // Task 4.6: Return userId for conversation history API
    userId: balanceData.userId || balanceData.user?.id || null
  };
};

/**
 * Send message to Q AI with SSE streaming (Enhanced for UI-6, UI-7, UI-8, Task 2.3/3.3, Symbol Context)
 * @param {string} token - JWT token
 * @param {string} message - User message
 * @param {string} actionType - 'chat' | 'analyze' | 'signal'
 * @param {string} selectedSymbol - Currently selected symbol for context-aware responses (e.g., 'BTCUSD_ct')
 * @param {function} onToken - Callback for each streamed token
 * @param {function} onDone - Callback when stream completes with new balance, conversationId, source
 * @param {function} onError - Callback for errors
 * @param {object} callbacks - Extended callbacks for UI-6/7/8
 * @param {function} callbacks.onStatus - Callback for status updates (step, progress, message)
 * @param {function} callbacks.onWarning - Callback for warnings (code, message, recoverable)
 * @param {function} callbacks.onReady - Callback when ready to generate
 * @param {string} conversationId - Optional conversation ID for multi-turn context (Task 2.3)
 * @returns {AbortController} - For canceling the request
 */
export const askQAI = (token, message, actionType, selectedSymbol, onToken, onDone, onError, callbacks = {}, conversationId = null) => {
  const controller = new AbortController();
  const { onStatus, onWarning, onReady } = callbacks;

  // Debug: Log token being used
  console.log('askQAI - Token:', token ? token.substring(0, 20) + '...' : 'NO TOKEN');
  if (selectedSymbol) {
    console.log('askQAI - SelectedSymbol:', selectedSymbol);
  }
  if (conversationId) {
    console.log('askQAI - ConversationId:', conversationId);
  }

  const fetchSSE = async () => {
    try {
      // Build request body with optional conversationId (Task 2.3) and selectedSymbol (Symbol Context)
      const requestBody = { message, actionType };

      // Symbol Context: Include selected symbol for context-aware responses
      if (selectedSymbol) {
        requestBody.symbol = selectedSymbol;
      }

      if (conversationId) {
        requestBody.conversationId = conversationId;
      }

      console.log('[qaiApi] askQAI request:', {
        message: message.substring(0, 50) + (message.length > 50 ? '...' : ''),
        actionType,
        symbol: selectedSymbol || '(none - will use all symbols)',
        conversationId: conversationId || '(none - new conversation)',
        hasConversationId: !!conversationId
      });

      const response = await fetch(`${baseURL}/api/q/ask`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'text/event-stream'
        },
        body: JSON.stringify(requestBody),
        signal: controller.signal
      });

      // Handle 401/403 auth errors
      if (response.status === 401 || response.status === 403) {
        handleAuthError(response.status, 'askQAI');
        onError({ code: response.status, message: 'Session expired. Please log in again.' });
        return;
      }

      // Handle 402 Payment Required (insufficient tokens)
      if (response.status === 402) {
        onError({ code: 402, message: 'Insufficient tokens' });
        return;
      }

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();

        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const jsonStr = line.slice(6).trim();
            if (jsonStr === '[DONE]') {
              continue;
            }

            try {
              const data = JSON.parse(jsonStr);

              // Handle different event types (UI-6, UI-7, UI-8)
              switch (data.type) {
                case 'status':
                  // Processing step update
                  if (onStatus) {
                    const stepInfo = PROCESSING_STEPS[data.step] || {};
                    onStatus({
                      step: data.step,
                      progress: data.progress ?? stepInfo.progress ?? 0,
                      message: data.message ?? stepInfo.message ?? 'Processing...'
                    });
                  }
                  break;

                case 'ready':
                  // Ready to generate (all data loaded)
                  if (onReady) {
                    onReady();
                  }
                  break;

                case 'token':
                  // AI response token
                  if (data.content) {
                    onToken(data.content);
                  }
                  break;

                case 'warning':
                  // Partial failure warning (UI-8)
                  if (onWarning) {
                    onWarning({
                      code: data.code,
                      message: data.message,
                      recoverable: data.recoverable ?? true
                    });
                  }
                  break;

                case 'error':
                  // Fatal error
                  onError({
                    code: data.code || 500,
                    message: data.message || 'Unknown error'
                  });
                  return;

                case 'done':
                  // Stream complete (Task 2.3/3.3: includes conversationId and source)
                  console.log('[qaiApi] SSE done event received:', {
                    conversationId: data.conversationId,
                    source: data.source,
                    newBalance: data.newBalance,
                    hasConversationId: !!data.conversationId
                  });
                  onDone({
                    newBalance: data.newBalance,
                    tokensUsed: data.tokensUsed,
                    responseTimeMs: data.responseTimeMs,
                    conversationId: data.conversationId,  // Task 2.3: conversation tracking
                    source: data.source                   // Task 3.3: LIVE | CACHED | FALLBACK
                  });
                  return;

                default:
                  // Legacy format support (token as direct property)
                  if (data.token) {
                    onToken(data.token);
                  }
                  if (data.done) {
                    console.log('[qaiApi] Legacy done event received:', {
                      conversationId: data.conversationId,
                      source: data.source,
                      hasConversationId: !!data.conversationId
                    });
                    onDone({
                      newBalance: data.newBalance,
                      tokensUsed: data.tokensUsed,
                      responseTimeMs: data.responseTimeMs,
                      conversationId: data.conversationId,  // Task 2.3
                      source: data.source                   // Task 3.3
                    });
                    return;
                  }
                  if (data.error) {
                    onError({ code: 500, message: data.error });
                    return;
                  }
              }
            } catch (e) {
              // Skip malformed JSON
            }
          }
        }
      }

      // Stream ended without explicit done
      onDone({ newBalance: null });
    } catch (error) {
      if (error.name === 'AbortError') {
        // Request was cancelled, don't call onError
        return;
      }
      onError({ code: 500, message: error.message });
    }
  };

  fetchSSE();
  return controller;
};

/**
 * Login with Google OAuth
 * @param {string} googleCredential - Google ID token from OAuth
 * @param {string} source - 'qai' for standalone, 'platform' for QTradeView
 * @returns {Promise<{token: string, user: object}>}
 */
export const googleLogin = async (googleCredential, source = 'qai') => {
  const response = await fetch(`${baseURL}/api/q/auth/google`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      credential: googleCredential,
      source
    })
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || `Login failed: ${response.status}`);
  }

  const result = await response.json();
  // Backend returns { success: true, data: { token, user } }
  return result.data || result;
};

/**
 * Login with email and password (for admin-created users)
 * @param {string} email - User email address
 * @param {string} password - User password
 * @returns {Promise<{token: string, user: object}>}
 */
export const emailLogin = async (email, password) => {
  const response = await fetch(`${baseURL}/api/q/auth/login`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ email, password })
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || error.data?.message || `Login failed: ${response.status}`);
  }

  const result = await response.json();
  // Backend returns { success: true, data: { token, user } }
  return result.data || result;
};

/**
 * Check Q AI service health
 * @returns {Promise<{status: string, claudeConfigured: boolean, timestamp: string}>}
 */
export const checkHealth = async () => {
  const response = await fetch(`${baseURL}/api/q/health`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json'
    }
  });

  if (!response.ok) {
    throw new Error(`Health check failed: ${response.status}`);
  }

  return response.json();
};

/**
 * Get cost for specific action type
 * @param {string} actionType - 'chat' | 'analyze' | 'signal'
 * @returns {number} - Token cost
 */
export const getActionCost = (actionType) => {
  return ACTION_COSTS[actionType] || ACTION_COSTS.chat;
};

// ═══════════════════════════════════════════════════════════════════════════
// QBRIDGE API FUNCTIONS (E9)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * QBridge download info - fallback values if API unavailable
 * CDN URL configured in: src/config/index.js
 */
const QBRIDGE_FALLBACK = {
  url: QBRIDGE_DOWNLOAD_URL,
  version: QBRIDGE_VERSION,
  size: QBRIDGE_SIZE,
  checksum: '',
  releaseNotes: 'v2.2.1 - Includes Q.AI Panel v3 (Canvas Input with scrolling) + v2.4 fallback. Polling fallback for reliable file detection, enhanced debug logging.',
  requirements: {
    os: 'Windows 10/11',
    mt5: 'MetaTrader 5 (any broker)'
  },
  includes: [
    'QBridge background service',
    'Q.AI Panel v3 EA for MT5',
    'Q.AI Panel v2.4 (stable fallback)',
    'Auto-starts on Windows boot'
  ],
  available: true // QBridge installer ready for download
};

/**
 * Get QBridge download information
 * @returns {Promise<{url: string, version: string, size: string, checksum: string, releaseNotes: string, requirements: object}>}
 */
export const getQBridgeDownload = async () => {
  try {
    const response = await fetch(`${baseURL}/api/q/qbridge/download`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      console.warn('QBridge download API unavailable, using fallback');
      return QBRIDGE_FALLBACK;
    }

    return response.json();
  } catch (error) {
    console.warn('Failed to fetch QBridge download info:', error);
    return QBRIDGE_FALLBACK;
  }
};

/**
 * Get QBridge sync status for current user
 * @param {string} token - JWT token
 * @returns {Promise<{hasSynced: boolean, lastSyncAt: string|null, tradeCount: number, accountInfo: object|null}>}
 */
export const getSyncStatus = async (token) => {
  try {
    const response = await fetch(`${baseURL}/api/q/sync-status`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      // Handle auth errors with redirect (don't throw, just redirect)
      if (handleAuthError(response.status, 'getSyncStatus')) {
        return { hasSynced: false, lastSyncAt: null, tradeCount: 0, accountInfo: null };
      }
      // Return default values if endpoint not available yet
      return {
        hasSynced: false,
        lastSyncAt: null,
        tradeCount: 0,
        accountInfo: null
      };
    }

    return response.json();
  } catch (error) {
    console.warn('Failed to fetch sync status:', error);
    return {
      hasSynced: false,
      lastSyncAt: null,
      tradeCount: 0,
      accountInfo: null
    };
  }
};

/**
 * Get Signal Board data for Q AI dashboard widgets
 * Endpoint: GET /api/q/board
 *
 * @param {string} token - JWT token
 * @returns {Promise<{signals: Array, updatedAt: string, signalCount: number}>}
 *
 * Response shape:
 * {
 *   signals: [{
 *     symbol: "EURUSD/GBPUSD",
 *     qSignal: "HOLD",
 *     qScore: 50,
 *     zScore: 2.77,
 *     zSignal: "STRONG_SELL",
 *     percentile: 5,
 *     regime: "LOW",
 *     entry: null,
 *     stopLoss: null,
 *     takeProfit: null,
 *     resistanceP95: -0.154,
 *     supportP5: -0.169,
 *     freshness: "FRESH",
 *     updatedAt: "2025-12-08T19:29:12Z",
 *     isEnriched: true
 *   }],
 *   updatedAt: "2025-12-08T19:29:14Z",
 *   signalCount: 3,
 *   enrichedCount: 3
 * }
 */
export const getSignalBoard = async (token) => {
  try {
    const response = await fetch(`${baseURL}/api/q/board`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      // Handle auth errors with redirect
      if (handleAuthError(response.status, 'getSignalBoard')) {
        return { signals: [], updatedAt: null, signalCount: 0, enrichedCount: 0 };
      }
      // Return empty state if endpoint not available
      return {
        signals: [],
        updatedAt: null,
        signalCount: 0,
        enrichedCount: 0
      };
    }

    const result = await response.json();

    // Handle wrapped response: { success, data: { signals: [...] } }
    if (result.success && result.data) {
      return {
        signals: result.data.signals || [],
        updatedAt: result.data.updatedAt || null,
        signalCount: result.data.signalCount || 0,
        enrichedCount: result.data.enrichedCount || 0
      };
    }

    // Fallback for direct response
    return {
      signals: result.signals || [],
      updatedAt: result.updatedAt || null,
      signalCount: result.signalCount || 0,
      enrichedCount: result.enrichedCount || 0
    };
  } catch (error) {
    console.warn('Failed to fetch signal board:', error);
    return {
      signals: [],
      updatedAt: null,
      signalCount: 0,
      enrichedCount: 0
    };
  }
};

/**
 * Check if QBridge connection is active
 * @param {string} token - JWT token
 * @returns {Promise<boolean>}
 */
export const checkQBridgeConnection = async (token) => {
  try {
    const status = await getSyncStatus(token);
    return status.hasSynced === true;
  } catch {
    return false;
  }
};

/**
 * Get user's synced trades from QBridge (E4)
 * @param {string} token - JWT token
 * @param {object} options - Query options
 * @param {number} options.limit - Max trades to return (default: 50)
 * @param {number} options.offset - Pagination offset (default: 0)
 * @param {string} options.sortBy - Sort field (default: 'closeTime')
 * @param {string} options.sortOrder - 'asc' | 'desc' (default: 'desc')
 * @returns {Promise<{trades: Array, total: number, hasMore: boolean}>}
 */
export const getUserTrades = async (token, options = {}) => {
  const { limit = 50, offset = 0, sortBy = 'closeTime', sortOrder = 'desc' } = options;

  try {
    const params = new URLSearchParams({
      limit: limit.toString(),
      offset: offset.toString(),
      sortBy,
      sortOrder
    });

    const response = await fetch(`${baseURL}/api/q/trades?${params}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Unauthorized');
      }
      // Return empty if endpoint not available
      return { trades: [], total: 0, hasMore: false };
    }

    const data = await response.json();
    return {
      trades: data.trades || data.data || [],
      total: data.total || 0,
      hasMore: data.hasMore || false
    };
  } catch (error) {
    console.warn('Failed to fetch user trades:', error);
    return { trades: [], total: 0, hasMore: false };
  }
};

// ═══════════════════════════════════════════════════════════════════════════
// PAYMENT API FUNCTIONS (F16)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Get pricing tiers and available payment providers
 * @returns {Promise<{tiers: Array, availableProviders: Array}>}
 */
export const getQAIPricing = async () => {
  try {
    const response = await fetch(`${baseURL}/api/q/pricing`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`Failed to get pricing: ${response.status}`);
    }

    const result = await response.json();
    return result.data || { tiers: PRICING_TIERS, availableProviders: [] };
  } catch (error) {
    console.warn('Failed to fetch pricing, using fallback:', error);
    // Fallback to local constants if API unavailable
    return { tiers: PRICING_TIERS, availableProviders: [] };
  }
};

/**
 * Create payment checkout session
 * @param {string} token - JWT token
 * @param {string} tierId - 'starter' | 'standard' | 'pro'
 * @param {string} provider - 'payoneer' | 'coinbase'
 * @param {string} returnUrl - URL to redirect after successful payment
 * @param {string} cancelUrl - URL to redirect if payment cancelled
 * @returns {Promise<{success: boolean, checkoutUrl: string, sessionId: string}>}
 */
export const createCheckout = async (token, tierId, provider, returnUrl, cancelUrl) => {
  const response = await fetch(`${baseURL}/api/q/topup`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      tierId,
      provider,
      returnUrl: returnUrl || `${window.location.origin}/q-ai/coach?payment=success`,
      cancelUrl: cancelUrl || `${window.location.origin}/q-ai/coach?payment=cancelled`
    })
  });

  const result = await response.json();

  if (!response.ok) {
    throw new Error(result.message || `Checkout failed: ${response.status}`);
  }

  return result.data || result;
};

// ═══════════════════════════════════════════════════════════════════════════
// IMPORT STATUS API FUNCTIONS (Issue #3/#4/#5)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Check if user has existing trade imports
 * Use this before showing "Import Trades" button to determine if user already imported
 *
 * @param {string} token - JWT token
 * @param {string} mt5Account - Optional MT5 account number to check
 * @returns {Promise<{
 *   hasExistingImport: boolean,
 *   lastImportAt: string|null,
 *   tradeCount: number,
 *   canImportNew: boolean,
 *   newTradesAvailable: number|null,
 *   message: string,
 *   accountNumber: string|null,
 *   broker: string|null
 * }>}
 */
export const checkImportStatus = async (token, mt5Account = null) => {
  try {
    const url = mt5Account
      ? `${baseURL}/api/q/import/check?mt5Account=${mt5Account}`
      : `${baseURL}/api/q/import/check`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Unauthorized');
      }
      // Return default "no imports" state if endpoint not available
      return {
        hasExistingImport: false,
        lastImportAt: null,
        tradeCount: 0,
        canImportNew: true,
        newTradesAvailable: null,
        message: 'No previous imports found',
        accountNumber: null,
        broker: null
      };
    }

    const result = await response.json();

    // Handle wrapped response: { success, data: {...} }
    const data = result.data || result;

    return {
      hasExistingImport: data.hasExistingImport || false,
      lastImportAt: data.lastImportAt || null,
      tradeCount: data.tradeCount || 0,
      canImportNew: data.canImportNew ?? true,
      newTradesAvailable: data.newTradesAvailable ?? null,
      message: data.message || '',
      accountNumber: data.accountNumber || null,
      broker: data.broker || null
    };
  } catch (error) {
    console.warn('Failed to check import status:', error);
    return {
      hasExistingImport: false,
      lastImportAt: null,
      tradeCount: 0,
      canImportNew: true,
      newTradesAvailable: null,
      message: 'Could not check import status',
      accountNumber: null,
      broker: null
    };
  }
};

/**
 * Poll import progress/completion status
 * Use after POST /api/q/trades/import to track import completion
 *
 * @param {string} token - JWT token
 * @param {string} importId - Import ID returned from trades/import endpoint
 * @returns {Promise<{
 *   status: 'pending'|'in_progress'|'completed'|'failed',
 *   importId: string,
 *   tradesImported: number,
 *   duplicatesSkipped: number,
 *   startedAt: string|null,
 *   completedAt: string|null,
 *   error: string|null,
 *   progress: number
 * }>}
 */
export const getImportProgressStatus = async (token, importId) => {
  try {
    const response = await fetch(`${baseURL}/api/q/import/status/${importId}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      if (response.status === 404) {
        return {
          status: 'failed',
          importId,
          tradesImported: 0,
          duplicatesSkipped: 0,
          startedAt: null,
          completedAt: null,
          error: 'Import not found',
          progress: 0
        };
      }
      throw new Error(`Failed to get import status: ${response.status}`);
    }

    const result = await response.json();
    const data = result.data || result;

    return {
      status: data.status || 'pending',
      importId: data.importId || importId,
      tradesImported: data.tradesImported || 0,
      duplicatesSkipped: data.duplicatesSkipped || 0,
      startedAt: data.startedAt || null,
      completedAt: data.completedAt || null,
      error: data.error || null,
      progress: data.progress || 0
    };
  } catch (error) {
    console.warn('Failed to get import progress:', error);
    return {
      status: 'failed',
      importId,
      tradesImported: 0,
      duplicatesSkipped: 0,
      startedAt: null,
      completedAt: null,
      error: error.message,
      progress: 0
    };
  }
};

/**
 * Get extended sync status with account info and import history
 * Enhanced version of getSyncStatus with more details
 *
 * @param {string} token - JWT token
 * @returns {Promise<{
 *   hasSynced: boolean,
 *   tradeCount: number,
 *   lastSyncAt: string|null,
 *   accountNumber: string|null,
 *   broker: string|null,
 *   isConnected: boolean,
 *   accountInfo: object|null,
 *   importHistory: Array,
 *   lastImportId: string|null,
 *   duplicatesSkipped: number
 * }>}
 */
export const getExtendedSyncStatus = async (token) => {
  try {
    const response = await fetch(`${baseURL}/api/q/sync-status/extended`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Unauthorized');
      }
      // Fall back to basic sync status if extended not available
      console.warn('Extended sync status not available, falling back to basic');
      const basicStatus = await getSyncStatus(token);
      return {
        ...basicStatus,
        accountNumber: null,
        broker: null,
        isConnected: basicStatus.hasSynced,
        importHistory: [],
        lastImportId: null,
        duplicatesSkipped: 0
      };
    }

    const result = await response.json();
    const data = result.data || result;

    return {
      hasSynced: data.hasSynced || false,
      tradeCount: data.tradeCount || 0,
      lastSyncAt: data.lastSyncAt || null,
      accountNumber: data.accountNumber || null,
      broker: data.broker || null,
      isConnected: data.isConnected || data.hasSynced || false,
      accountInfo: data.accountInfo || null,
      importHistory: data.importHistory || [],
      lastImportId: data.lastImportId || null,
      duplicatesSkipped: data.duplicatesSkipped || 0
    };
  } catch (error) {
    console.warn('Failed to fetch extended sync status:', error);
    return {
      hasSynced: false,
      tradeCount: 0,
      lastSyncAt: null,
      accountNumber: null,
      broker: null,
      isConnected: false,
      accountInfo: null,
      importHistory: [],
      lastImportId: null,
      duplicatesSkipped: 0
    };
  }
};

/**
 * Helper: Poll import status until completion
 * Automatically polls and resolves when import completes or fails
 *
 * @param {string} token - JWT token
 * @param {string} importId - Import ID to poll
 * @param {object} options - Polling options
 * @param {number} options.interval - Polling interval in ms (default: 1000)
 * @param {number} options.timeout - Max time to wait in ms (default: 60000)
 * @param {function} options.onProgress - Callback for progress updates
 * @returns {Promise<{success: boolean, tradesImported: number, error: string|null}>}
 */
export const pollImportUntilComplete = async (token, importId, options = {}) => {
  const {
    interval = 1000,
    timeout = 60000,
    onProgress = null
  } = options;

  const startTime = Date.now();

  return new Promise((resolve) => {
    const poll = async () => {
      // Check timeout
      if (Date.now() - startTime > timeout) {
        resolve({
          success: false,
          tradesImported: 0,
          error: 'Import timed out'
        });
        return;
      }

      const status = await getImportProgressStatus(token, importId);

      // Call progress callback if provided
      if (onProgress) {
        onProgress(status);
      }

      if (status.status === 'completed') {
        resolve({
          success: true,
          tradesImported: status.tradesImported,
          duplicatesSkipped: status.duplicatesSkipped,
          error: null
        });
      } else if (status.status === 'failed') {
        resolve({
          success: false,
          tradesImported: 0,
          error: status.error || 'Import failed'
        });
      } else {
        // Still in progress, poll again
        setTimeout(poll, interval);
      }
    };

    poll();
  });
};

// ═══════════════════════════════════════════════════════════════════════════
// CONVERSATION HISTORY API FUNCTIONS (Task 4.2/4.3)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Get user's conversation history list
 * Endpoint: GET /api/admin/qai/conversations?userId={userId}&limit=20
 *
 * @param {string} token - JWT token
 * @param {string} userId - User ID (obtained from getBalance response)
 * @param {object} options - Query options
 * @param {number} options.limit - Max conversations to return (default: 20)
 * @param {number} options.offset - Pagination offset (default: 0)
 * @returns {Promise<{conversations: Array, total: number, hasMore: boolean}>}
 *
 * Response shape:
 * {
 *   conversations: [{
 *     conversationId: "qai_f1832343_...",
 *     title: "Trading rules for XAUUSD" | null,
 *     createdAt: "2025-12-08T19:29:12Z",
 *     updatedAt: "2025-12-08T19:30:00Z",
 *     messageCount: 4,
 *     lastMessage: "Explain rule 2..."
 *   }],
 *   total: 15,
 *   hasMore: false
 * }
 */
export const getUserConversations = async (token, userId, options = {}) => {
  const { limit = 20, offset = 0 } = options;

  try {
    const params = new URLSearchParams({
      userId,
      limit: limit.toString(),
      offset: offset.toString()
    });

    const response = await fetch(`${qaiAdminBaseUrl}${qaiAdminPathPrefix}/conversations?${params}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      // Handle auth errors with redirect
      if (handleAuthError(response.status, 'getUserConversations')) {
        return { conversations: [], total: 0, hasMore: false };
      }
      // Return empty if endpoint not available
      console.warn('[qaiApi] getUserConversations failed:', response.status);
      return { conversations: [], total: 0, hasMore: false };
    }

    const result = await response.json();
    console.log('[qaiApi] getUserConversations raw response:', JSON.stringify(result, null, 2));

    const data = result.data || result;
    console.log('[qaiApi] getUserConversations extracted data:', JSON.stringify(data, null, 2));

    // Map response to consistent format
    const rawConversations = data.conversations || data || [];
    console.log('[qaiApi] Raw conversations array:', JSON.stringify(rawConversations, null, 2));

    const conversations = rawConversations.map(conv => {
      console.log('[qaiApi] Mapping conversation:', JSON.stringify(conv, null, 2));
      const mapped = {
        conversationId: conv.conversationId || conv.id,
        title: conv.title || null,  // May be null - UI will show "Untitled" or first message
        createdAt: conv.createdAt,
        updatedAt: conv.updatedAt || conv.createdAt,
        messageCount: conv.messageCount || 0,
        lastMessage: conv.lastMessage || conv.preview || null
      };
      console.log('[qaiApi] Mapped to:', JSON.stringify(mapped, null, 2));
      return mapped;
    });

    return {
      conversations,
      total: data.total || conversations.length,
      hasMore: data.hasMore || false
    };
  } catch (error) {
    console.warn('[qaiApi] Failed to fetch user conversations:', error);
    return { conversations: [], total: 0, hasMore: false };
  }
};

/**
 * Get full conversation history by ID
 * Endpoint: GET /api/admin/qai/conversations/{conversationId}
 *
 * @param {string} token - JWT token
 * @param {string} conversationId - Conversation ID to fetch
 * @returns {Promise<{
 *   conversationId: string,
 *   messages: Array<{role: string, content: string, timestamp: string}>,
 *   createdAt: string,
 *   updatedAt: string,
 *   title: string|null
 * }>}
 */
export const getConversationHistory = async (token, conversationId) => {
  console.log('[qaiApi] getConversationHistory called:', conversationId);

  try {
    const url = `${qaiAdminBaseUrl}${qaiAdminPathPrefix}/conversations/${conversationId}`;
    console.log('[qaiApi] Fetching from URL:', url);

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    console.log('[qaiApi] Response status:', response.status);

    if (!response.ok) {
      // Handle auth errors with redirect
      if (handleAuthError(response.status, 'getConversationHistory')) {
        return { messages: [], conversationId: null };
      }
      if (response.status === 404) {
        throw new Error('Conversation not found');
      }
      throw new Error(`Failed to get conversation: ${response.status}`);
    }

    const result = await response.json();
    console.log('[qaiApi] Raw API response:', JSON.stringify(result, null, 2));

    const data = result.data || result;
    console.log('[qaiApi] Extracted data:', JSON.stringify(data, null, 2));

    // Map messages to consistent format for UI
    const messages = (data.messages || []).map((msg, index) => ({
      id: msg.id || `${conversationId}-${index}`,
      role: msg.role,  // 'user' | 'assistant'
      content: msg.content,
      timestamp: msg.timestamp || msg.createdAt
    }));

    console.log('[qaiApi] Mapped messages count:', messages.length);

    return {
      conversationId: data.conversationId || conversationId,
      messages,
      createdAt: data.createdAt,
      updatedAt: data.updatedAt || data.createdAt,
      title: data.title || null
    };
  } catch (error) {
    console.error('[qaiApi] Failed to fetch conversation history:', error);
    throw error;  // Re-throw so UI can handle specific errors
  }
};

// ═══════════════════════════════════════════════════════════════════════════
// MT5 ACCOUNT BINDING API FUNCTIONS (v5.2)
// One MT5 account per user enforcement
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Check if user has a bound MT5 account and validate against detected account
 * Endpoint: GET /api/q/mt5-account/check?detectedAccount={mt5Account}
 *
 * @param {string} token - JWT token
 * @param {string} detectedAccount - MT5 account number detected by QBridge (optional)
 * @returns {Promise<{
 *   hasBoundAccount: boolean,
 *   boundAccount: string|null,
 *   boundBroker: string|null,
 *   boundAt: string|null,
 *   isMatch: boolean,
 *   canBind: boolean,
 *   message: string
 * }>}
 *
 * Response scenarios:
 * 1. No bound account yet → { hasBoundAccount: false, canBind: true }
 * 2. Bound account matches → { hasBoundAccount: true, isMatch: true }
 * 3. Bound account differs → { hasBoundAccount: true, isMatch: false, canBind: false }
 */
export const checkMT5AccountBinding = async (token, detectedAccount = null) => {
  try {
    const params = new URLSearchParams();
    if (detectedAccount) {
      params.append('detectedAccount', detectedAccount);
    }

    const url = `${baseURL}/api/q/mt5-account/check${params.toString() ? '?' + params.toString() : ''}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      // Handle auth errors with redirect
      if (handleAuthError(response.status, 'checkMT5AccountBinding')) {
        return {
          hasBoundAccount: false,
          boundAccount: null,
          boundBroker: null,
          boundAt: null,
          isMatch: false,
          canBind: false,
          message: 'Session expired'
        };
      }

      // If endpoint not available yet, allow binding (graceful degradation)
      if (response.status === 404) {
        console.warn('[qaiApi] MT5 account binding endpoint not available yet');
        return {
          hasBoundAccount: false,
          boundAccount: null,
          boundBroker: null,
          boundAt: null,
          isMatch: true,  // Allow to proceed
          canBind: true,
          message: 'Account binding not enforced'
        };
      }

      throw new Error(`Failed to check MT5 binding: ${response.status}`);
    }

    const result = await response.json();
    const data = result.data || result;

    return {
      hasBoundAccount: data.hasBoundAccount || false,
      boundAccount: data.boundAccount || null,
      boundBroker: data.boundBroker || null,
      boundAt: data.boundAt || null,
      isMatch: data.isMatch ?? true,  // Default true if not checking
      canBind: data.canBind ?? !data.hasBoundAccount,
      message: data.message || ''
    };
  } catch (error) {
    console.warn('[qaiApi] Failed to check MT5 account binding:', error);
    // Graceful degradation - allow to proceed if check fails
    return {
      hasBoundAccount: false,
      boundAccount: null,
      boundBroker: null,
      boundAt: null,
      isMatch: true,
      canBind: true,
      message: 'Could not verify account binding'
    };
  }
};

/**
 * Bind MT5 account to user (first-time binding only)
 * Endpoint: POST /api/q/mt5-account/bind
 *
 * @param {string} token - JWT token
 * @param {string} mt5Account - MT5 account number to bind
 * @param {string} broker - Broker name (optional)
 * @returns {Promise<{success: boolean, message: string, boundAccount: string|null}>}
 */
export const bindMT5Account = async (token, mt5Account, broker = null) => {
  try {
    const response = await fetch(`${baseURL}/api/q/mt5-account/bind`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        mt5Account,
        broker
      })
    });

    if (!response.ok) {
      // Handle auth errors
      if (handleAuthError(response.status, 'bindMT5Account')) {
        return { success: false, message: 'Session expired', boundAccount: null };
      }

      // Handle conflict (already bound to different account)
      if (response.status === 409) {
        const errorData = await response.json().catch(() => ({}));
        return {
          success: false,
          message: errorData.message || 'This email is already linked to a different MT5 account',
          boundAccount: errorData.boundAccount || null,
          alreadyBound: true
        };
      }

      // If endpoint not available, succeed silently (graceful degradation)
      if (response.status === 404) {
        console.warn('[qaiApi] MT5 account binding endpoint not available yet');
        return { success: true, message: 'Account binding not enforced', boundAccount: mt5Account };
      }

      throw new Error(`Failed to bind MT5 account: ${response.status}`);
    }

    const result = await response.json();
    const data = result.data || result;

    return {
      success: data.success ?? true,
      message: data.message || 'MT5 account linked successfully',
      boundAccount: data.boundAccount || mt5Account
    };
  } catch (error) {
    console.warn('[qaiApi] Failed to bind MT5 account:', error);
    // Graceful degradation - report success if binding fails
    return {
      success: true,
      message: 'Account binding not enforced',
      boundAccount: mt5Account
    };
  }
};

/**
 * Delete a conversation (excluded from v1.0 - kept for future use)
 * Endpoint: DELETE /api/admin/qai/conversations/{conversationId}
 *
 * @param {string} token - JWT token
 * @param {string} conversationId - Conversation ID to delete
 * @returns {Promise<{success: boolean}>}
 */
export const deleteConversation = async (token, conversationId) => {
  try {
    const response = await fetch(`${qaiAdminBaseUrl}${qaiAdminPathPrefix}/conversations/${conversationId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Unauthorized');
      }
      if (response.status === 403) {
        throw new Error('Access denied - not your conversation');
      }
      if (response.status === 404) {
        // Already deleted - treat as success
        return { success: true };
      }
      throw new Error(`Failed to delete conversation: ${response.status}`);
    }

    return { success: true };
  } catch (error) {
    console.error('[qaiApi] Failed to delete conversation:', error);
    throw error;
  }
};
